import { Routes } from '@angular/router';

import { authGuard } from './auth/guards/auth.guard.ts-guard';
import { permissionGuard } from './auth/guards/permission.guard';
import { preReleaseGuard } from './auth/guards/pre-release.guard';
import { unsavedChangesGuard } from './auth/guards/unsaved-changes.guard';

export const routes: Routes = [
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.page').then((m) => m.HomePage),
    canActivate: [authGuard],
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.page').then((m) => m.LoginPage),
  },
  {
    // Regreso de "Continuar con Google": pública como `login`, la sesión
    // todavía no existe cuando Cognito llega aquí con el código.
    path: 'login/callback',
    loadComponent: () =>
      import('./pages/login-callback/login-callback.page').then((m) => m.LoginCallbackPage),
  },
  {
    // Página pública de regreso de un link de pago: el cliente llega sin sesión,
    // igual que a `login`, así que no lleva authGuard. El token opaco `t` de la
    // query es lo que autoriza la consulta al back (2.3.1 del plan de link de
    // pago); esta ruta solo la sirve.
    path: 'pagos/confirmacion',
    loadComponent: () =>
      import('./pages/payment-link-confirmation/payment-link-confirmation.page').then(
        (m) => m.PaymentLinkConfirmationPage,
      ),
  },
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'kwh-dashboard',
    loadComponent: () =>
      import('./pages/kwh-dashboard/kwh-dashboard.page').then((m) => m.KwhDashboardPage),
    canActivate: [authGuard],
  },
  {
    path: 'users',
    loadComponent: () => import('./pages/users/users.page').then((m) => m.UsersPage),
    // `edit_users` y no `view_users`: la pantalla es de administración, mientras que
    // `view_users` es la lectura del directorio que necesita el selector de agentes
    // de Cobranza. Ver el comentario del item "Usuarios" en sidebar.component.html.
    canActivate: [authGuard, permissionGuard('edit_users')],
  },
  {
    path: 'customers',
    loadComponent: () => import('./pages/customers/customers.page').then((m) => m.CustomersPage),
    canActivate: [authGuard],
  },
  {
    path: 'customers/consolidation',
    loadComponent: () =>
      import('./pages/customer-consolidation/customer-consolidation.page').then(
        (m) => m.CustomerConsolidationPage,
      ),
    canActivate: [authGuard],
  },
  // Grupo App Clientes (clientes.terraenergy.mx): rutas bajo `customer-app/*`
  // para que los módulos que vengan de la app se cuelguen del mismo prefijo.
  {
    path: 'customer-app/accounts',
    loadComponent: () =>
      import('./pages/customer-app-accounts/customer-app-accounts.page').then(
        (m) => m.CustomerAppAccountsPage,
      ),
    canActivate: [authGuard, permissionGuard('view_customer_app_accounts')],
  },
  {
    path: 'catalogs',
    loadComponent: () => import('./pages/catalogs/catalogs.page').then((m) => m.CatalogsPage),
    canActivate: [authGuard],
  },
  {
    path: 'systems',
    loadComponent: () => import('./pages/systems/systems.page').then((m) => m.SystemsPage),
    canActivate: [authGuard],
  },
  {
    // Ampliaciones de un sistema ya instalado: salen del listado de sistemas y se
    // consultan aquí. Sin permiso propio, igual que «Sistemas».
    path: 'addendums',
    loadComponent: () => import('./pages/addendums/addendums.page').then((m) => m.AddendumsPage),
    canActivate: [authGuard],
  },
  {
    path: 'system-detail/:id',
    loadComponent: () =>
      import('./pages/system-detail/system-detail.page').then((m) => m.SystemDetailPage),
    canActivate: [authGuard],
  },
  {
    // Atajo para cobranza: `/folio/12` resuelve el folio TL (systems.id_legacy) y
    // redirige al detalle del sistema en Aurora. Ruta aparte porque los dos
    // numeradores se traslapan y `/system-detail/:id` ya está publicado por todos lados.
    path: 'folio/:folio',
    loadComponent: () =>
      import('./pages/legacy-folio-redirect/legacy-folio-redirect.page').then(
        (m) => m.LegacyFolioRedirectPage,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'customer-detail/:id',
    loadComponent: () =>
      import('./pages/customer-detail/customer-detail.page').then((m) => m.CustomerDetailPage),
    canActivate: [authGuard],
  },
  {
    // Ruta de detalle de usuario — carga lazy del componente UserDetailPage
    path: 'user-detail/:id',
    loadComponent: () =>
      import('./pages/user-detail/user-detail.page').then((m) => m.UserDetailPage),
    // Mismo guard que el listado: se llega desde ahí y permite editar al usuario.
    canActivate: [authGuard, permissionGuard('edit_users')],
  },
  {
    path: 'payment-receipt-detail/:id',
    loadComponent: () =>
      import('./pages/payment-receipt-detail/payment-receipt-detail.page').then(
        (m) => m.PaymentReceiptDetailPage,
      ),
    canActivate: [authGuard, permissionGuard('view_payment_receipts')],
  },
  {
    path: 'invoice-detail/:id',
    loadComponent: () =>
      import('./pages/invoice-detail/invoice-detail.page').then((m) => m.InvoiceDetailPage),
    canActivate: [authGuard],
  },
  {
    // Sin permissionGuard: las gráficas son de lectura y el gating por permiso
    // del grupo de Gráficas está pendiente. Desde el 2026-09-21 esta ruta sirve
    // el reporte construido con SAC; las gráficas de sistemas por periodo/
    // estatus se retiraron.
    path: 'charts/general',
    loadComponent: () =>
      import('./pages/charts-general/charts-general.page').then((m) => m.ChartsGeneralPage),
    canActivate: [authGuard, permissionGuard('view_charts')],
  },
  {
    path: 'billing-playground',
    loadComponent: () =>
      import('./pages/billing-playground/billing-playground.page').then(
        (m) => m.BillingPlaygroundPage,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'collections/processing',
    loadComponent: () =>
      import('./pages/collection-processing/collection-processing.page').then(
        (m) => m.CollectionProcessingPage,
      ),
    canActivate: [authGuard, permissionGuard('view_collection_processing')],
  },
  {
    // Parámetros del cobro automático (día de cobro de Venta de Energía). Mismo
    // permiso de lectura que Pasarelas de pago; editar exige además
    // `manage_collection_processing`, que valida la propia tarjeta.
    path: 'collections/processing/config',
    loadComponent: () =>
      import('./pages/collection-processing-config/collection-processing-config.page').then(
        (m) => m.CollectionProcessingConfigPage,
      ),
    canActivate: [authGuard, permissionGuard('view_collection_processing')],
  },
  {
    // Los dos parámetros identifican el recurso: la pantalla no existe sin ambos.
    // Es la primera ruta del repo con dos, y no colisiona con la de arriba —
    // aquélla es una hoja, y en Angular una hoja no consume segmentos sobrantes.
    path: 'collections/processing/retry/:gateway/:scheme',
    loadComponent: () =>
      import('./pages/collection-retry-invoices/collection-retry-invoices.page').then(
        (m) => m.CollectionRetryInvoicesPage,
      ),
    canActivate: [authGuard, permissionGuard('view_collection_processing')],
  },
  {
    // Contracargos con tarjeta. Se llega desde el widget "Disputas" de Pasarelas de
    // pago; pide su propio permiso porque es el mismo que protege el endpoint.
    path: 'collections/processing/chargebacks',
    loadComponent: () =>
      import('./pages/collection-chargebacks/collection-chargebacks.page').then(
        (m) => m.CollectionChargebacksPage,
      ),
    canActivate: [authGuard, permissionGuard('view_chargebacks')],
  },
  {
    path: 'collections/direct-debit',
    loadComponent: () =>
      import('./pages/collections-direct-debit/collections-direct-debit.page').then(
        (m) => m.CollectionsDirectDebitPage,
      ),
    canActivate: [authGuard, permissionGuard('view_direct_debit')],
  },
  {
    path: 'collections/billing',
    loadComponent: () =>
      import('./pages/collections-billing/collections-billing.page').then(
        (m) => m.CollectionsBillingPage,
      ),
    canActivate: [authGuard, permissionGuard('view_invoices')],
  },
  {
    path: 'settings/developer/integrations/webhooks/create',
    loadComponent: () =>
      import('./pages/webhooks-create/webhooks-create.page').then((m) => m.WebhooksCreatePage),
    canActivate: [authGuard, permissionGuard('create_webhooks')],
  },
  {
    path: 'settings/developer/integrations/webhooks/:id',
    loadComponent: () =>
      import('./pages/webhook-detail/webhook-detail.page').then((m) => m.WebhookDetailPage),
    canActivate: [authGuard, permissionGuard('view_webhooks')],
  },
  {
    // Service Desk en Configuración → Desarrollador: instrucciones de la IA que clasifica tickets.
    path: 'settings/developer/ticket-classifier',
    loadComponent: () =>
      import('./pages/settings-ticket-classifier/settings-ticket-classifier.page').then(
        (m) => m.SettingsTicketClassifierPage,
      ),
    canActivate: [
      authGuard,
      permissionGuard('view_settings'),
      permissionGuard('manage_it_ticket_settings'),
    ],
  },
  {
    path: 'settings/developer/jobs',
    loadComponent: () => import('./pages/jobs/jobs.page').then((m) => m.JobsPage),
    // Gate con el permiso de ACCION y no con uno de lectura: la pantalla existe
    // para operar switches, no informa nada que otra vista no de. Sin el guard,
    // la URL escrita a mano entraba igual y la pantalla se pintaba vacia.
    canActivate: [authGuard, permissionGuard('toggle_job_switches')],
  },
  {
    // Administración de las tablas precalculadas de las gráficas de Pasarelas de
    // pago; se entra desde Configuración › Cobranza. Regenerar y limpiar exigen además
    // `manage_payments_analytics`, que la página revisa por su cuenta.
    path: 'settings/collection/analytics',
    loadComponent: () =>
      import('./pages/payments-analytics/payments-analytics.page').then(
        (m) => m.PaymentsAnalyticsPage,
      ),
    canActivate: [authGuard, permissionGuard('view_payments_analytics')],
  },
  // Pantallas de Configuración → Marketing, con el permiso con que antes se veía su widget
  {
    path: 'settings/marketing/lead-capture',
    loadComponent: () =>
      import('./pages/settings-lead-capture/settings-lead-capture.page').then(
        (m) => m.SettingsLeadCapturePage,
      ),
    canActivate: [
      authGuard,
      preReleaseGuard,
      permissionGuard('view_settings'),
      permissionGuard('view_leads'),
    ],
  },
  {
    path: 'settings/marketing/lead-duplicates',
    loadComponent: () =>
      import('./pages/settings-lead-duplicates/settings-lead-duplicates.page').then(
        (m) => m.SettingsLeadDuplicatesPage,
      ),
    canActivate: [
      authGuard,
      preReleaseGuard,
      permissionGuard('view_settings'),
      permissionGuard('view_leads'),
    ],
  },
  {
    // Administración de la tabla precalculada de Marketing → Adquisición; se entra
    // desde Configuración › Marketing. Gate con el permiso de acción: la pantalla existe para regenerar.
    path: 'settings/marketing/leads-analytics',
    loadComponent: () =>
      import('./pages/leads-analytics/leads-analytics.page').then((m) => m.LeadsAnalyticsPage),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('manage_lead_acquisition_analytics')],
  },
  // Catálogos de adquisición (TE-84): proveedores y bitácora son solo lectura; los canales se
  // administran con manage_lead_acquisition_channels dentro de la pantalla.
  {
    path: 'settings/marketing/tags',
    data: { taggableType: 'lead' },
    loadComponent: () =>
      import('./pages/settings-tags/settings-tags.page').then((m) => m.SettingsTagsPage),
    canActivate: [
      authGuard,
      preReleaseGuard,
      permissionGuard('view_settings'),
      permissionGuard('manage_tags'),
    ],
  },
  {
    path: 'settings/marketing/channels',
    loadComponent: () =>
      import('./pages/marketing-acquisition-channels/marketing-acquisition-channels.page').then(
        (m) => m.MarketingAcquisitionChannelsPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_lead_acquisition_channels')],
  },
  {
    path: 'settings/marketing/acquisition-providers',
    loadComponent: () =>
      import('./pages/marketing-acquisition-providers/marketing-acquisition-providers.page').then(
        (m) => m.MarketingAcquisitionProvidersPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_lead_acquisition_channels')],
  },
  {
    path: 'settings/marketing/acquisition-providers/:code',
    loadComponent: () =>
      import('./pages/marketing-acquisition-provider-detail/marketing-acquisition-provider-detail.page').then(
        (m) => m.MarketingAcquisitionProviderDetailPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_lead_acquisition_channels')],
  },
  {
    path: 'settings/marketing/acquisition-events',
    loadComponent: () =>
      import('./pages/marketing-acquisition-events/marketing-acquisition-events.page').then(
        (m) => m.MarketingAcquisitionEventsPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_lead_acquisition_channels')],
  },
  {
    // Ver usa el mismo permiso que los catálogos; editar pide manage_lead_acquisition_rules (T.I.).
    path: 'settings/marketing/acquisition-rules',
    loadComponent: () =>
      import('./pages/settings-bleik-pull-rules/settings-bleik-pull-rules.page').then(
        (m) => m.SettingsBleikPullRulesPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_lead_acquisition_channels')],
    canDeactivate: [unsavedChangesGuard],
  },
  {
    path: 'settings/collection/legacy-sync',
    loadComponent: () =>
      import('./pages/legacy-sync/legacy-sync.page').then((m) => m.LegacySyncPage),
    canActivate: [authGuard, permissionGuard('view_legacy_sync')],
  },
  {
    // Inventario global de tokens de tarjeta; la columna Data trae el JSON crudo de la
    // pasarela, por eso el permiso lo tiene solo T.I.
    path: 'settings/collection/tokens',
    loadComponent: () =>
      import('./pages/payment-method-tokens/payment-method-tokens.page').then(
        (m) => m.PaymentMethodTokensPage,
      ),
    canActivate: [authGuard, permissionGuard('view_payment_method_tokens')],
  },
  {
    path: 'settings/collection/transactions',
    loadComponent: () =>
      import('./pages/transactions/transactions.page').then((m) => m.TransactionsPage),
    canActivate: [authGuard],
  },
  {
    // Una sola página con 4 secciones internas (ion-segment) — cada sección
    // revisa su propio permiso por su cuenta; el guard de la ruta solo exige
    // AL MENOS UNO de los 7 de asset_management (ver 05-panel-administracion.md).
    path: 'asset-management/loyalty',
    loadComponent: () =>
      import('./pages/loyalty-admin/loyalty-admin.page').then((m) => m.LoyaltyAdminPage),
    canActivate: [
      authGuard,
      permissionGuard([
        'view_loyalty_catalog',
        'manage_loyalty_catalog',
        'view_loyalty_balances',
        'adjust_loyalty_points',
        'view_loyalty_redemptions',
        'resolve_loyalty_redemptions',
        'review_loyalty_receipts',
      ]),
    ],
  },
  {
    // Recursos Humanos › Organigrama Empresarial. Gateada con `view_org_chart`
    // (migración 062: super_admin, direccion y hr_manager). Editar el lugar de
    // una persona pide además `edit_users`, que la página revisa por su cuenta.
    path: 'rh/organigrama',
    loadComponent: () => import('./pages/org-chart/org-chart.page').then((m) => m.OrgChartPage),
    canActivate: [authGuard, permissionGuard('view_org_chart')],
  },
  {
    // Recursos Humanos › Expedientes. Gateada con `view_employee_files`
    // (migración 097: super_admin y hr_manager). Lleva datos bancarios y de salud.
    path: 'rh/expedientes',
    loadComponent: () =>
      import('./pages/hr-employee-records/hr-employee-records.page').then(
        (m) => m.HrEmployeeRecordsPage,
      ),
    canActivate: [authGuard, permissionGuard('view_employee_files')],
  },
  {
    path: 'rh/expedientes/:id',
    loadComponent: () =>
      import('./pages/hr-employee-record-detail/hr-employee-record-detail.page').then(
        (m) => m.HrEmployeeRecordDetailPage,
      ),
    canActivate: [authGuard, permissionGuard('view_employee_files')],
  },
  {
    // Mi perfil: documentos propios, vacaciones y cuentas conectadas. Cualquier
    // usuario con sesión; el back solo devuelve el expediente ligado a su cuenta.
    path: 'mi-perfil',
    loadComponent: () => import('./pages/my-profile/my-profile.page').then((m) => m.MyProfilePage),
    canActivate: [authGuard],
  },
  {
    path: 'collections/payment-receipts',
    loadComponent: () =>
      import('./pages/payment-receipts-approval/payment-receipts-approval.page').then(
        (m) => m.PaymentReceiptsApprovalPage,
      ),
    canActivate: [authGuard, permissionGuard('approve_payment_receipts')],
  },
  {
    path: 'collections/credit-balances',
    loadComponent: () =>
      import('./pages/collections-credit-balances/collections-credit-balances.page').then(
        (m) => m.CollectionsCreditBalancesPage,
      ),
    canActivate: [authGuard, permissionGuard('view_payment_receipts')],
  },
  {
    // Cobranza › Meses gratis: las órdenes que Terralink marcó como mes gratis y
    // su bonificación. Mismo permiso que la sección de cobranza del sistema,
    // de donde salen los datos (`GET /collection-orders`).
    path: 'collections/free-months',
    loadComponent: () =>
      import('./pages/collections-free-months/collections-free-months.page').then(
        (m) => m.CollectionsFreeMonthsPage,
      ),
    canActivate: [authGuard, permissionGuard('view_collection_orders')],
  },
  {
    // Cobranza › Facturación: la facturación del mes por esquema que lanza el
    // Gerente de Cobranza. Ver y preparar la vista previa piden
    // `view_collection_invoicing`; lanzar, pausar, reanudar, cancelar y reintentar
    // piden además `run_collection_invoicing`, que la página revisa por su cuenta.
    path: 'collections/invoicing',
    loadComponent: () =>
      import('./pages/collections-invoicing/collections-invoicing.page').then(
        (m) => m.CollectionsInvoicingPage,
      ),
    canActivate: [authGuard, permissionGuard('view_collection_invoicing')],
  },
  {
    path: 'collections/agent-assignments',
    loadComponent: () =>
      import('./pages/collections-agent-assignments/collections-agent-assignments.page').then(
        (m) => m.CollectionsAgentAssignmentsPage,
      ),
    canActivate: [authGuard, permissionGuard('view_agent_assignments')],
  },
  {
    path: 'collections/pending-tasks',
    loadComponent: () =>
      import('./pages/collections-pending-tasks/collections-pending-tasks.page').then(
        (m) => m.CollectionsPendingTasksPage,
      ),
    canActivate: [authGuard, permissionGuard('view_pending_tasks')],
  },
  {
    // Sin permissionGuard a propósito: el gating por permiso del dashboard de
    // KPIs va en la fase de RBAC (acordado con cobranza, 9-sep-2026).
    path: 'collections/agent-kpis',
    loadComponent: () =>
      import('./pages/collections-agent-kpis/collections-agent-kpis.page').then(
        (m) => m.CollectionsAgentKpisPage,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'settings/developer/integrations',
    loadComponent: () =>
      import('./pages/integrations/integrations.page').then((m) => m.IntegrationsPage),
    canActivate: [authGuard, permissionGuard('view_integrations')],
  },
  {
    path: 'settings/security/roles',
    loadComponent: () =>
      import('./pages/roles-permissions/roles-permissions.page').then(
        (m) => m.RolesPermissionsPage,
      ),
    canActivate: [authGuard, permissionGuard('view_roles')],
  },
  {
    path: 'settings/security/roles/all',
    loadComponent: () =>
      import('./pages/all-permissions/all-permissions.page').then((m) => m.AllPermissionsPage),
    canActivate: [authGuard, permissionGuard('view_permissions')],
  },
  {
    path: 'settings',
    loadComponent: () => import('./pages/settings/settings.page').then((m) => m.SettingsPage),
    canActivate: [authGuard, permissionGuard('view_settings')],
  },
  {
    path: 'settings/business/sepomex',
    loadComponent: () =>
      import('./pages/settings-sepomex/settings-sepomex.page').then((m) => m.SettingsSepomexPage),
    // Dos guards y no `permissionGuard([...])`: con arreglo basta UNO de los permisos,
    // y aquí hacen falta los dos (el endpoint de cobertura pide `view_coverage`).
    canActivate: [authGuard, permissionGuard('view_settings'), permissionGuard('view_coverage')],
  },
  {
    path: 'settings/business/cfe-tariffs',
    loadComponent: () =>
      import('./pages/settings-cfe-tariffs/settings-cfe-tariffs.page').then(
        (m) => m.SettingsCfeTariffsPage,
      ),
    // Los dos permisos: con arreglo bastaría uno, y el endpoint pide `view_quotes`.
    canActivate: [authGuard, permissionGuard('view_settings'), permissionGuard('view_quotes')],
  },
  // Pantallas de Configuración → Ventas, con el mismo permiso con que antes se veía su widget
  {
    path: 'settings/sales/quote-parameters',
    loadComponent: () =>
      import('./pages/settings-quote-parameters/settings-quote-parameters.page').then(
        (m) => m.SettingsQuoteParametersPage,
      ),
    canActivate: [
      authGuard,
      preReleaseGuard,
      permissionGuard('view_settings'),
      permissionGuard('manage_quote_settings'),
    ],
  },
  {
    path: 'settings/sales/tags',
    data: { taggableType: 'quote' },
    loadComponent: () =>
      import('./pages/settings-tags/settings-tags.page').then((m) => m.SettingsTagsPage),
    canActivate: [
      authGuard,
      preReleaseGuard,
      permissionGuard('view_settings'),
      permissionGuard('manage_tags'),
    ],
  },
  {
    path: 'settings/sales/quote-customization',
    loadComponent: () =>
      import('./pages/settings-quote-customization/settings-quote-customization.page').then(
        (m) => m.SettingsQuoteCustomizationPage,
      ),
    canActivate: [
      authGuard,
      preReleaseGuard,
      permissionGuard('view_settings'),
      permissionGuard('manage_quotes'),
    ],
  },
  {
    path: 'settings/sales/bill-extraction',
    loadComponent: () =>
      import('./pages/settings-bill-extraction/settings-bill-extraction.page').then(
        (m) => m.SettingsBillExtractionPage,
      ),
    canActivate: [
      authGuard,
      preReleaseGuard,
      permissionGuard('view_settings'),
      permissionGuard('view_data_extraction'),
    ],
  },
  {
    path: 'settings/sales/coverage-approval',
    loadComponent: () =>
      import('./pages/settings-coverage-approval/settings-coverage-approval.page').then(
        (m) => m.SettingsCoverageApprovalPage,
      ),
    canActivate: [
      authGuard,
      preReleaseGuard,
      permissionGuard('view_settings'),
      permissionGuard('manage_sales_coverage_settings'),
    ],
  },
  {
    path: 'settings/sales/pricing',
    loadComponent: () =>
      import('./pages/settings-pricing/settings-pricing.page').then((m) => m.SettingsPricingPage),
    canActivate: [authGuard, permissionGuard('view_settings'), permissionGuard('view_pricing')],
  },
  {
    path: 'settings/sales/pricing/:id',
    loadComponent: () =>
      import('./pages/settings-pricing-detail/settings-pricing-detail.page').then(
        (m) => m.SettingsPricingDetailPage,
      ),
    canActivate: [authGuard, permissionGuard('view_settings'), permissionGuard('view_pricing')],
  },
  {
    path: 'tickets',
    redirectTo: 'tickets/ti',
    pathMatch: 'full',
  },
  {
    // Sin permiso: la ruta es de cualquier autenticado a propósito. Quien no es
    // de T.I. entra igual —a lo suyo— y gatear el acceso dejaría la feature
    // inservible para casi toda la empresa, que hoy no tiene permisos asignados.
    path: 'tickets/ti',
    loadComponent: () => import('./pages/it-tickets/it-tickets.page').then((m) => m.ItTicketsPage),
    canActivate: [authGuard],
  },
  {
    // Antes de `tickets/ti/:id`: Angular toma la primera coincidencia y
    // "kpis" encajaría como un id. Gateada con `view_ticket_kpis`, que ya
    // tienen T.I., super_admin y administrator.
    path: 'tickets/ti/kpis',
    loadComponent: () =>
      import('./pages/it-agent-kpis/it-agent-kpis.page').then((m) => m.ItAgentKpisPage),
    canActivate: [authGuard, permissionGuard('view_ticket_kpis')],
  },
  {
    // También antes de `tickets/ti/:id`. Pesos por área y tiempos de atención;
    // `manage_it_ticket_settings` lo tienen it_support_agent y super_admin.
    path: 'tickets/ti/configuracion',
    loadComponent: () =>
      import('./pages/it-ticket-settings/it-ticket-settings.page').then(
        (m) => m.ItTicketSettingsPage,
      ),
    canActivate: [authGuard, permissionGuard('manage_it_ticket_settings')],
  },
  {
    path: 'tickets/ti/:id',
    loadComponent: () =>
      import('./pages/it-ticket-detail/it-ticket-detail.page').then((m) => m.ItTicketDetailPage),
    canActivate: [authGuard],
  },
  {
    path: 'sales/quotes',
    loadComponent: () =>
      import('./pages/sales-quotes/sales-quotes.page').then((m) => m.SalesQuotesPage),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_quotes')],
  },
  {
    path: 'sales/coverage',
    loadComponent: () =>
      import('./pages/sales-coverage/sales-coverage.page').then((m) => m.SalesCoveragePage),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_coverage')],
  },
  {
    path: 'supply-chain/analytics',
    loadComponent: () =>
      import('./pages/supply-chain-analytics/supply-chain-analytics.page').then(
        (m) => m.SupplyChainAnalyticsPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_supply_chain_analytics')],
  },
  {
    // Estado y regeneración de los analíticos de Supply Chain; se entra desde
    // Configuración › Supply Chain. Gate con el permiso de acción: la pantalla existe para regenerar.
    path: 'settings/supply-chain/analytics',
    loadComponent: () =>
      import('./pages/supply-chain-analytics-admin/supply-chain-analytics-admin.page').then(
        (m) => m.SupplyChainAnalyticsAdminPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('manage_supply_chain_analytics')],
  },
  {
    path: 'supply-chain/availability',
    loadComponent: () =>
      import('./pages/supply-chain-availability/supply-chain-availability.page').then(
        (m) => m.SupplyChainAvailabilityPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_panel_availability')],
  },
  {
    path: 'supply-chain/items',
    loadComponent: () =>
      import('./pages/supply-chain-items/supply-chain-items.page').then(
        (m) => m.SupplyChainItemsPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_inventory_catalog')],
  },
  {
    path: 'sales/coverage/requests/:id',
    loadComponent: () =>
      import('./pages/sales-coverage-request/sales-coverage-request.page').then(
        (m) => m.SalesCoverageRequestPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_coverage')],
  },
  {
    // Antes de `sales/quotes/:id`: "new" encajaría como un id.
    path: 'sales/quotes/new',
    loadComponent: () =>
      import('./pages/sales-quote-editor/sales-quote-editor.page').then(
        (m) => m.SalesQuoteEditorPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('manage_quotes')],
  },
  {
    path: 'sales/quotes/:id',
    loadComponent: () =>
      import('./pages/sales-quote-editor/sales-quote-editor.page').then(
        (m) => m.SalesQuoteEditorPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_quotes')],
  },
  {
    path: 'marketing/leads',
    loadComponent: () =>
      import('./pages/marketing-leads/marketing-leads.page').then((m) => m.MarketingLeadsPage),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_leads')],
  },
  {
    // Antes de `marketing/leads/:id`: "new" encajaría como un id.
    path: 'marketing/leads/new',
    loadComponent: () =>
      import('./pages/marketing-lead-detail/marketing-lead-detail.page').then(
        (m) => m.MarketingLeadDetailPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('manage_leads')],
  },
  {
    // Antes de `marketing/leads/:id`: "referrals" encajaría como un id.
    path: 'marketing/leads/referrals',
    loadComponent: () =>
      import('./pages/marketing-lead-referrals/marketing-lead-referrals.page').then(
        (m) => m.MarketingLeadReferralsPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('manage_lead_referrals')],
  },
  {
    path: 'marketing/leads/:id',
    loadComponent: () =>
      import('./pages/marketing-lead-detail/marketing-lead-detail.page').then(
        (m) => m.MarketingLeadDetailPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_leads')],
  },
  {
    path: 'marketing/blocked-emails',
    loadComponent: () =>
      import('./pages/marketing-blocked-emails/marketing-blocked-emails.page').then(
        (m) => m.MarketingBlockedEmailsPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_lead_email_blocklist')],
  },
  {
    path: 'marketing/lead-costs',
    loadComponent: () =>
      import('./pages/marketing-lead-costs/marketing-lead-costs.page').then(
        (m) => m.MarketingLeadCostsPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_lead_acquisition_costs')],
  },
  {
    path: 'marketing/acquisition',
    loadComponent: () =>
      import('./pages/marketing-acquisition/marketing-acquisition.page').then(
        (m) => m.MarketingAcquisitionPage,
      ),
    canActivate: [authGuard, preReleaseGuard, permissionGuard('view_lead_acquisition_analytics')],
  },
  {
    path: 'mailing/campaigns',
    loadComponent: () =>
      import('./pages/mailing-campaigns/mailing-campaigns.page').then(
        (m) => m.MailingCampaignsPage,
      ),
    canActivate: [authGuard, permissionGuard('view_mailings')],
  },
  {
    // Antes de `mailing/campaigns/:id`: Angular toma la primera coincidencia y
    // "new" encajaría como un id. Crear exige `send_mailings`, no basta leer.
    path: 'mailing/campaigns/new',
    loadComponent: () =>
      import('./pages/mailing-campaign-editor/mailing-campaign-editor.page').then(
        (m) => m.MailingCampaignEditorPage,
      ),
    canActivate: [authGuard, permissionGuard('send_mailings')],
  },
  {
    path: 'mailing/campaigns/:id',
    loadComponent: () =>
      import('./pages/mailing-campaign-detail/mailing-campaign-detail.page').then(
        (m) => m.MailingCampaignDetailPage,
      ),
    canActivate: [authGuard, permissionGuard('view_mailings')],
  },
  {
    // Misma página que `new`: solo un borrador se edita, y eso lo valida la
    // propia página contra el estatus de la campaña.
    path: 'mailing/campaigns/:id/edit',
    loadComponent: () =>
      import('./pages/mailing-campaign-editor/mailing-campaign-editor.page').then(
        (m) => m.MailingCampaignEditorPage,
      ),
    canActivate: [authGuard, permissionGuard('send_mailings')],
  },
  {
    // Lectura para cualquiera que vea campañas; cargar, editar y borrar listas
    // exige además `manage_mailing_lists`, que la página revisa por su cuenta.
    path: 'mailing/lists',
    loadComponent: () =>
      import('./pages/mailing-folio-lists/mailing-folio-lists.page').then(
        (m) => m.MailingFolioListsPage,
      ),
    canActivate: [authGuard, permissionGuard('view_mailings')],
  },
  {
    // Lectura para cualquiera que vea campañas; crear y editar plantillas exige
    // además `manage_mailing_templates`, que la página revisa por su cuenta.
    path: 'mailing/templates',
    loadComponent: () =>
      import('./pages/mailing-templates/mailing-templates.page').then(
        (m) => m.MailingTemplatesPage,
      ),
    canActivate: [authGuard, permissionGuard('view_mailings')],
  },
  {
    // Comunicación interna: a quién le llega cada aviso que manda Aurora al
    // personal. Vive en el menú de Comunicación, pero solo la ve T.I.
    path: 'internal-notifications',
    loadComponent: () =>
      import('./pages/internal-notifications/internal-notifications.page').then(
        (m) => m.InternalNotificationsPage,
      ),
    canActivate: [authGuard, permissionGuard('manage_internal_notifications')],
  },
  {
    // En caso de que no coincida ninguna ruta, redirigimos a login
    path: '**',
    redirectTo: 'login',
  },
];
